import { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';

/**
 * Subscribes to Firebase auth state and resolves the user's role.
 *
 * Roles del backoffice:
 *   - 'admin' → users/{uid}.ADMIN === true. Acceso completo: lectura + escritura.
 *   - 'viewer' → users/{uid}.ADMIN_VIEWER === true. Solo lectura, info sensible
 *                enmascarada (email, @tiktok) en la UI.
 *   - null     → sin acceso al backoffice.
 *
 * El estado se resuelve UNA sola vez en <AuthProvider> (montado en main.jsx) y
 * se comparte por contexto. Así las páginas reciben el rol ya resuelto desde su
 * primer render en vez de arrancar con role=null y hacer su propia lectura a
 * Firestore. Antes, si los datos de una tabla llegaban antes que el rol, NextUI
 * cacheaba las celdas en modo solo lectura (p. ej. solo el botón "Perfil" en
 * Users) y no las volvía a pintar al cambiar canWrite.
 *
 * Retorna:
 *   user           Firebase user o null
 *   role           'admin' | 'viewer' | null
 *   isAdmin        role === 'admin'      (full admin)
 *   isViewer       role === 'viewer'     (read-only)
 *   canAccess      role !== null         (entra al backoffice)
 *   canWrite       role === 'admin'      (puede modificar)
 *   loading        true mientras resuelve
 *   logout()       signOut
 */

const AuthContext = createContext(null);

function useAuthState() {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        try {
          const snap = await getDoc(doc(db, 'users', u.uid));
          const data = snap.exists() ? snap.data() : {};
          if (data.ADMIN === true) setRole('admin');
          else if (data.ADMIN_VIEWER === true) setRole('viewer');
          else setRole(null);
        } catch (e) {
          console.warn('[useAuth] role lookup failed:', e?.message);
          setRole(null);
        }
      } else {
        setRole(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  return {
    user,
    role,
    isAdmin: role === 'admin',
    isViewer: role === 'viewer',
    canAccess: role !== null,
    canWrite: role === 'admin',
    loading,
    logout: () => signOut(auth)
  };
}

export function AuthProvider({ children }) {
  const value = useAuthState();
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth() debe usarse dentro de <AuthProvider> (ver main.jsx)');
  }
  return ctx;
}
